import React, { type MouseEvent } from 'react';
import { styled } from '@compiled/react';
import Button from '@atlaskit/button';
import CrossIcon from '@atlaskit/icon/glyph/cross';
import MediaServicesActualSizeIcon from '@atlaskit/icon/glyph/media-services/actual-size';
import MediaServicesFitToPageIcon from '@atlaskit/icon/glyph/media-services/fit-to-page';
import { N0, N800 } from '@atlaskit/theme/colors';
import { token } from '@atlaskit/tokens';
import { useIntl } from 'react-intl-next';
import { ModalMode } from '../../../common/types';
import { DockIcon } from './assets/dock-icon';
import { messages } from './messages';

type Props = {
	title?: string;
	mode: ModalMode;
	onClose: () => void;
	onModeChange: (mode: ModalMode) => void;
	onPrevModeChange?: () => void;
};

export const ModalHeader = ({ title, mode, onClose, onModeChange, onPrevModeChange }: Props) => {
	const { formatMessage } = useIntl();

	const handleHeaderClick = () => {
		if (mode !== ModalMode.DOCKED) {
			return;
		}

		onPrevModeChange?.();
	};

	const handleClose = (e: MouseEvent) => {
		e.stopPropagation();
		onClose();
	};

	return (
		<Container
			data-testid="experiment-standard-differentiators-minimizable-modal.ui.content.header.container"
			isDocked={mode === ModalMode.DOCKED}
			onClick={handleHeaderClick}
		>
			<Title data-testid="experiment-standard-differentiators-minimizable-modal.ui.content.header.title">
				{title}
			</Title>
			{mode !== ModalMode.DOCKED && (
				<Button
					testId="experiment-standard-differentiators-minimizable-modal.ui.content.header.button-dock"
					spacing="none"
					label={formatMessage(messages.dock)}
					appearance="subtle"
					iconBefore={<DockIcon label="" size="medium" primaryColor={token('color.icon', N800)} />}
					onClick={() => onModeChange(ModalMode.DOCKED)}
				/>
			)}
			{mode === ModalMode.DEFAULT && (
				<Button
					testId="experiment-standard-differentiators-minimizable-modal.ui.content.header.button-expand"
					spacing="none"
					label={formatMessage(messages.expand)}
					appearance="subtle"
					iconBefore={
						<MediaServicesActualSizeIcon
							label=""
							size="medium"
							primaryColor={token('color.icon', N800)}
						/>
					}
					onClick={() => onModeChange(ModalMode.EXPANDED)}
				/>
			)}
			{mode === ModalMode.EXPANDED && (
				<Button
					testId="experiment-standard-differentiators-minimizable-modal.ui.content.header.button-minimize"
					spacing="none"
					label={formatMessage(messages.minimize)}
					appearance="subtle"
					iconBefore={
						<MediaServicesFitToPageIcon
							label=""
							size="medium"
							primaryColor={token('color.icon', N800)}
						/>
					}
					onClick={() => onModeChange(ModalMode.DEFAULT)}
				/>
			)}
			<Button
				testId="experiment-standard-differentiators-minimizable-modal.ui.content.header.button-close"
				spacing="none"
				appearance="subtle"
				label={formatMessage(messages.close)}
				iconBefore={<CrossIcon label="" size="medium" primaryColor={token('color.icon', N0)} />}
				onClick={handleClose}
			/>
		</Container>
	);
};

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const Container = styled.div<{ isDocked: boolean }>({
	background: `linear-gradient(84.09deg, ${token(
		'color.background.accent.blue.subtler',
		'#CCE0FF',
	)} 0%, ${token('color.background.accent.purple.subtler', '#DFD8FD')} 100%)`,
	height: '40px',
	boxSizing: 'border-box',
	padding: `${token('space.050', '4px')} ${token('space.200', '16px')}`,
	display: 'flex',
	alignItems: 'center',
	gap: token('space.100', '8px'),
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-dynamic-styles -- Ignored via go/DSP-18766
	cursor: ({ isDocked }) => isDocked && 'pointer',
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const Title = styled.div({
	fontWeight: 600,
	fontSize: '12px',
	lineHeight: '16px',
	color: token('color.text', N800),
	marginRight: 'auto',
});
